/**
 * Slurp treasury batch fee claim.
 *
 * Builds one claimPositionFee instruction per pool, packs them into
 * transactions until the per-tx account budget is full, and submits each
 * tx sequentially. Each pool's claim is roughly 11 accounts after dedup,
 * so we conservatively cap at 4 claims/tx — well under the 64-account
 * tx limit, leaving room for the recent blockhash + fee payer + ATA
 * creation if needed.
 *
 * Returns an array of { mint, sig?, error? } so the UI can show per-pool
 * results. Failures don't abort the run — each tx is independent.
 */

import {
  Connection,
  Transaction,
  PublicKey,
  ComputeBudgetProgram,
} from "@solana/web3.js";
import {
  TOKEN_PROGRAM_ID,
  TOKEN_2022_PROGRAM_ID,
  getAssociatedTokenAddressSync,
} from "@solana/spl-token";

const RPC_URL =
  process.env.REACT_APP_SOLANA_RPC_URL ||
  "https://api.mainnet-beta.solana.com";
const CLAIMS_PER_TX = 4;
const COMPUTE_LIMIT = 600_000;
const PRIORITY_FEE_MICROLAMPORTS = 50_000;

const loadCpAmm = async () => {
  const mod = await import("@meteora-ag/cp-amm-sdk");
  return mod;
};

/**
 * Build the claim ix(s) for a single pool. Returns an array of
 * TransactionInstruction so we can chain multiple pools per tx.
 *
 * The slurp position NFT was transferred at launch into the treasury's
 * regular Token-2022 ATA (see solanaLaunch.js — `slurpFinalNftAta`), NOT
 * the SDK's `derivePositionNftAccount` PDA. We must pass that real ATA as
 * `positionNftAccount` or the cp-amm program rejects with ConstraintRaw.
 */
async function buildClaimIxs({
  cpAmm,
  owner,
  pool,
  positionStr,
  positionNftMintStr,
}) {
  const position = new PublicKey(positionStr);
  const nftMint = new PublicKey(positionNftMintStr);
  const nftAccount = getAssociatedTokenAddressSync(
    nftMint,
    owner,
    true, // allowOwnerOffCurve — treasury could be any pubkey
    TOKEN_2022_PROGRAM_ID
  );
  const poolPk = new PublicKey(pool);
  const poolState = await cpAmm.fetchPoolState(poolPk);
  const tx = await cpAmm.claimPositionFee({
    owner,
    position,
    pool: poolPk,
    positionNftAccount: nftAccount,
    tokenAMint: poolState.tokenAMint,
    tokenBMint: poolState.tokenBMint,
    tokenAVault: poolState.tokenAVault,
    tokenBVault: poolState.tokenBVault,
    tokenAProgram: TOKEN_PROGRAM_ID,
    tokenBProgram: TOKEN_PROGRAM_ID,
  });
  return tx.instructions;
}

/**
 * Claim multiple slurp positions in batched transactions.
 *
 * @param {Object} args
 * @param {Object} args.wallet           wallet adapter — needs publicKey + signTransaction
 * @param {Array}  args.pools            [{ mint, pool, slurp_position, slurp_position_nft }]
 * @param {Function=} args.onProgress    ({ index, total, mint, sig, error }) => void
 * @returns {Promise<Array<{mint, sig?, error?}>>}
 */
export async function batchClaimSlurpFees({ wallet, pools, onProgress }) {
  if (!wallet?.publicKey) throw new Error("Treasury wallet not connected");
  if (!Array.isArray(pools) || pools.length === 0) return [];
  if (typeof wallet.signTransaction !== "function") {
    throw new Error("Wallet does not support signTransaction");
  }

  const connection = new Connection(RPC_URL, "confirmed");
  const { CpAmm } = await loadCpAmm();
  const cpAmm = new CpAmm(connection);
  const owner = wallet.publicKey;

  // Build instructions per pool (parallel — these are read-only RPCs).
  const built = await Promise.all(
    pools.map(async (p) => {
      try {
        const ixs = await buildClaimIxs({
          cpAmm,
          owner,
          pool: p.pool,
          positionStr: p.slurp_position,
          positionNftMintStr: p.slurp_position_nft,
        });
        return { mint: p.mint, ixs };
      } catch (err) {
        return { mint: p.mint, error: err?.message || String(err) };
      }
    })
  );

  const results = [];
  // Group successful builds into chunks; each chunk → one tx.
  const claimable = built.filter((b) => !b.error && b.ixs?.length);
  const failures = built.filter((b) => b.error);
  for (const f of failures) {
    results.push({ mint: f.mint, error: f.error });
    onProgress?.({ ...f, total: built.length, index: results.length });
  }

  for (let i = 0; i < claimable.length; i += CLAIMS_PER_TX) {
    const chunk = claimable.slice(i, i + CLAIMS_PER_TX);
    const tx = new Transaction();
    tx.add(
      ComputeBudgetProgram.setComputeUnitLimit({ units: COMPUTE_LIMIT }),
      ComputeBudgetProgram.setComputeUnitPrice({
        microLamports: PRIORITY_FEE_MICROLAMPORTS,
      })
    );
    for (const c of chunk) tx.add(...c.ixs);
    tx.feePayer = owner;
    const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash("confirmed");
    tx.recentBlockhash = blockhash;

    try {
      const signed = await wallet.signTransaction(tx);
      const sig = await connection.sendRawTransaction(signed.serialize(), {
        skipPreflight: false,
        maxRetries: 3,
      });
      await connection.confirmTransaction(
        { signature: sig, blockhash, lastValidBlockHeight },
        "confirmed"
      );
      for (const c of chunk) {
        results.push({ mint: c.mint, sig });
        onProgress?.({ mint: c.mint, sig, total: built.length, index: results.length });
      }
    } catch (err) {
      const msg = err?.message || String(err);
      for (const c of chunk) {
        results.push({ mint: c.mint, error: msg });
        onProgress?.({ mint: c.mint, error: msg, total: built.length, index: results.length });
      }
    }
  }

  return results;
}

/** Convenience — claim a single position. Same signing path as the batch. */
export async function claimSingleSlurpFee({ wallet, pool }) {
  const [r] = await batchClaimSlurpFees({ wallet, pools: [pool] });
  if (r?.error) throw new Error(r.error);
  return r;
}
